import React, { useEffect, useState, Component } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { AlertTriangleIcon, LockIcon } from 'lucide-react';
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[]; // Optional array of roles, if any are matched, access is granted
  requireAuth?: boolean; // If true, requires authentication (defaults to true)
  redirectTo?: string; // Where to redirect if access is denied (defaults to '/')
  showAccessDenied?: boolean; // Whether to show access denied instead of redirecting
}
/**
 * ProtectedRoute component that restricts access based on authentication and roles
 *
 * @example
 * // Basic authentication protection
 * <ProtectedRoute>
 *   <SensitivePage />
 * </ProtectedRoute>
 *
 * @example
 * // Role-based protection (seller only)
 * <ProtectedRoute requiredRoles={['seller']}>
 *   <SellerDashboard />
 * </ProtectedRoute>
 *
 * @example
 * // Multiple role options (either role grants access)
 * <ProtectedRoute requiredRoles={['admin', 'moderator']}>
 *   <AdminPanel />
 * </ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requireAuth = true,
  redirectTo = '/',
  showAccessDenied = false
}) => {
  const {
    isAuthenticated,
    user,
    loading,
    hasRole
  } = useAuth();
  const location = useLocation();
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  // Check if user has the required roles
  useEffect(() => {
    // Skip check if still loading
    if (loading) return;
    // If auth is not required, grant access
    if (!requireAuth) {
      setHasAccess(true);
      setAccessChecked(true);
      return;
    }
    // Check authentication
    if (!isAuthenticated) {
      setHasAccess(false);
      setAccessChecked(true);
      return;
    }
    // If no specific roles required, just being authenticated is enough
    if (requiredRoles.length === 0) {
      setHasAccess(true);
      setAccessChecked(true);
      return;
    }
    // Check if user has any of the required roles (OR logic)
    const userHasRequiredRole = requiredRoles.some(role => hasRole(role));
    setHasAccess(userHasRequiredRole);
    setAccessChecked(true);
  }, [isAuthenticated, user, loading, requiredRoles, requireAuth, hasRole]);
  // Show loading state while checking access
  if (loading || !accessChecked) {
    return <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-white dark:bg-[#0F1F1D]" data-id="element-196">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489] mb-4" data-id="element-197"></div>
        <p className="text-gray-600 dark:text-[#B0C1BD] text-center" data-id="element-198">
          Verifying access...
        </p>
      </div>;
  }
  // If access is denied and we want to show an access denied screen
  if (!hasAccess && showAccessDenied) {
    return <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-white dark:bg-[#0F1F1D]" data-id="element-199">
        <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-6" data-id="element-200">
          <LockIcon className="h-10 w-10 text-red-600 dark:text-red-400" data-id="element-201" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4" data-id="element-202">
          Access Denied
        </h1>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-lg p-4 mb-6 max-w-md" data-id="element-203">
          <div className="flex" data-id="element-204">
            <AlertTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" data-id="element-205" />
            <div data-id="element-206">
              <p className="text-sm text-red-700 dark:text-red-300" data-id="element-207">
                {!isAuthenticated ? 'You need to be signed in to access this page.' : "You don't have permission to access this page."}
              </p>
              {!isAuthenticated && <p className="text-sm text-red-700 dark:text-red-300 mt-2" data-id="element-208">
                  Please sign in to continue.
                </p>}
              {isAuthenticated && requiredRoles.length > 0 && <p className="text-sm text-red-700 dark:text-red-300 mt-2" data-id="element-209">
                  Required role: {requiredRoles.join(' or ')}
                </p>}
            </div>
          </div>
        </div>
        <div className="flex space-x-4" data-id="element-210">
          <button onClick={() => window.history.back()} className="px-6 py-2 bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] rounded-lg hover:bg-gray-200 dark:hover:bg-[#0B2C2A] transition-colors" data-id="element-211">
            Go Back
          </button>
          {!isAuthenticated && <button onClick={() => window.location.href = '/sell-with-us?openAuth=true'} className="px-6 py-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-lg hover:shadow-md transition-all" data-id="element-212">
              Sign In
            </button>}
        </div>
      </div>;
  }
  // If access is denied, redirect
  if (!hasAccess) {
    // Save the attempted URL for redirect after login
    const currentPath = location.pathname + location.search + location.hash;
    const loginRedirect = '/sell-with-us?openAuth=true&redirect=' + encodeURIComponent(currentPath);
    // Redirect to login page or specified redirect location
    return <Navigate to={!isAuthenticated ? loginRedirect : redirectTo} replace data-id="element-213" />;
  }
  // If access is granted, render the protected content
  return <>{children}</>;
};
export default ProtectedRoute;